import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Help } from 'ui/icons';
import { FormField, HelpText } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { Tooltip } from 'ui/tooltip';
import { spacing } from 'ui/theme';

import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { Button, EmbeddedContentDefinition } from 'modules/graphql';
import { FieldNames } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useValidateContentField } from 'modules/templateEditor/hooks/useValidateContentField';

import { DISPLAY_CONTENT, EmbeddedActionButton } from '../../constants';
import { ActionOption } from '../../types';
import { getButtonActionType } from '../../utils';
import { RadioWithInputText } from '../components';
import { ActionsInputContainer, RadioOptionsContainer } from './styles';

const {
  actionButtonCloseMessageTooltipContent,
  actionButtonIdentifierHelpText,
  actionButtonIdentifierLabel,
  actionButtonIdentifierPlaceholder,
  actionButtonInputsLabel,
  actionButtonTypeClose,
  actionButtonTypeCustom,
  actionButtonTypeUrl,
  openActionCustomHelpText,
  openActionUrlHelpText,
  urlPlaceholder,
} = DISPLAY_CONTENT.settings;
const { URL, CLOSE, CUSTOM } = EmbeddedActionButton;

type Props = {
  button: Button;
  hasError: boolean;
  index: number;
};

export const ActionsInput = ({ button, hasError: hasFieldError, index }: Props) => {
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { initialTemplate, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { elements } = liveTemplate.mediumData as EmbeddedContentDefinition;
  const actionButtons = elements?.buttons || [];
  const selectedActionType = getButtonActionType(button.action);

  const { errorText: fieldErrorText } = useValidateContentField(FieldNames.ACTION_BUTTONS);

  const initialButton = useMemo(
    () => (initialTemplate.mediumData as EmbeddedContentDefinition)?.elements?.buttons?.[index],
    [initialTemplate],
  );

  const [identifier, setIdentifier] = useState(initialButton?.id || '');

  const setActionButtons = (updatedActions: Button[]) => {
    const newElements = {
      ...elements,
      buttons: updatedActions,
    };
    setCurrentMediumField('elements', newElements);
  };

  const updateActionButton = (updatedButton: Button) => {
    const actionButtonsList = [...actionButtons];
    actionButtonsList[index] = updatedButton;
    setActionButtons(actionButtonsList);
  };

  const handleUrlChange = (url: string) => {
    updateActionButton({ ...button, action: { type: URL, data: url } });
  };

  const handleCustomActionChange = (inputValue: string) => {
    updateActionButton({ ...button, action: { type: inputValue, data: '' } });
  };

  const handleRadioClick = (actionType: EmbeddedActionButton) => {
    const didSelectInitialType = getButtonActionType(initialButton?.action) === actionType;

    if (actionType === CLOSE)
      return updateActionButton({ ...button, action: { type: CLOSE, data: '' } });

    const action =
      didSelectInitialType && initialButton
        ? initialButton.action
        : { type: actionType === URL ? actionType : '', data: '' };
    updateActionButton({ ...button, action });
  };

  const getActionButtonDisplayLabel = (actionType: EmbeddedActionButton) => {
    switch (actionType) {
      case CLOSE:
        return (
          <>
            {actionButtonTypeClose}
            <Tooltip tooltipContent={actionButtonCloseMessageTooltipContent}>
              <Help height={spacing.plusHalf} width={spacing.base} />
            </Tooltip>
          </>
        );
      case CUSTOM:
        return actionButtonTypeCustom;
      default:
        return actionButtonTypeUrl;
    }
  };

  const actionButtonInputOptions: ActionOption[] = useMemo(
    () => [
      {
        id: URL,
        errorText: fieldErrorText,
        hasError: hasFieldError,
        inputText: {
          helpText: openActionUrlHelpText,
          isDisplayed: selectedActionType === URL,
          onChange: handleUrlChange,
          placeholder: urlPlaceholder,
          value: button?.action?.data || '',
        },
      },
      /** CLOSE option to be reinserted once BE support is ready */
      // { id: CLOSE },
      /** Custom actions support in SDK will be released alongside placements */
      ...(isEmbeddedMessagingEnabled
        ? [
            {
              id: CUSTOM,
              errorText: fieldErrorText,
              hasError: hasFieldError,
              inputText: {
                helpText: openActionCustomHelpText,
                isDisplayed: selectedActionType === CUSTOM,
                onChange: handleCustomActionChange,
                placeholder: urlPlaceholder,
                value: button?.action?.type || '',
              },
            },
          ]
        : []),
    ],
    [actionButtons, elements, hasFieldError],
  );

  useEffect(() => {
    updateActionButton({ ...button, id: identifier });
  }, [identifier]);

  return (
    <ActionsInputContainer>
      <FormField labelText={actionButtonInputsLabel}>
        <RadioOptionsContainer>
          {actionButtonInputOptions.map(({ errorText, hasError, id, inputText }) => (
            <RadioWithInputText
              key={`radio-option-action-button-${id}`}
              id={`action-button-${id}`}
              errorText={errorText}
              hasError={hasError}
              inputText={inputText}
              label={getActionButtonDisplayLabel(id as EmbeddedActionButton)}
              onClick={() => handleRadioClick(id as EmbeddedActionButton)}
              optionId={id}
              selectedOption={selectedActionType}
              value={button}
            />
          ))}
        </RadioOptionsContainer>
      </FormField>
      <FormField labelText={actionButtonIdentifierLabel}>
        <InputText
          onChange={setIdentifier}
          placeholder={actionButtonIdentifierPlaceholder}
          value={identifier}
        />
        <HelpText>{actionButtonIdentifierHelpText}</HelpText>
      </FormField>
    </ActionsInputContainer>
  );
};
